import { useEffect, useState } from "preact/hooks";
import { api, ApiError } from "../api";
import type {
  ActivityLevel,
  Goal,
  OnboardingInput,
  Sex,
  TargetCalcResult,
} from "@hh/shared";

const GOALS: { v: Goal; label: string }[] = [
  { v: "fat_loss", label: "Lose fat" },
  { v: "muscle_gain", label: "Build muscle" },
  { v: "recomp", label: "Recomp" },
  { v: "maintain", label: "Maintain" },
];

const ACTIVITY: { v: ActivityLevel; label: string }[] = [
  { v: "sedentary", label: "Sedentary" },
  { v: "light", label: "Light" },
  { v: "moderate", label: "Moderate" },
  { v: "active", label: "Active" },
  { v: "very_active", label: "Very active" },
];

const lbToKg = (lb: number) => Math.round((lb / 2.2046) * 10) / 10;
const ftInToCm = (ft: number, inch: number) => Math.round((ft * 12 + inch) * 2.54);

export function Onboarding({ onDone }: { onDone: () => void }) {
  // Defaults seeded from the brief's reference user; the user edits freely.
  const [age, setAge] = useState(36);
  const [ft, setFt] = useState(6);
  const [inch, setInch] = useState(0);
  const [weightLb, setWeightLb] = useState(200);
  const [sex, setSex] = useState<Sex>("male");
  const [goal, setGoal] = useState<Goal>("fat_loss");
  const [activity, setActivity] = useState<ActivityLevel>("moderate");

  const [preview, setPreview] = useState<TargetCalcResult | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const input = (): OnboardingInput => ({
    age,
    height_cm: ftInToCm(ft, inch),
    sex,
    current_weight: lbToKg(weightLb),
    goal,
    activity_level: activity,
  });

  // Live target preview as inputs change.
  useEffect(() => {
    let cancelled = false;
    api.onboardingPreview(input())
      .then((r) => { if (!cancelled) setPreview(r); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [age, ft, inch, weightLb, sex, goal, activity]);

  async function commit() {
    setErr("");
    setBusy(true);
    try {
      await api.onboarding(input());
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="app-shell fade-in">
      <div class="screen-head">
        <div>
          <div class="eyebrow">Let's set you up</div>
          <h1>Your numbers</h1>
        </div>
      </div>

      <div class="card">
        <div class="field">
          <label>Goal</label>
          <div class="seg">
            {GOALS.map((g) => (
              <button key={g.v} class={goal === g.v ? "active" : ""} onClick={() => setGoal(g.v)}>
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <div class="field">
          <label>Age</label>
          <input type="number" inputMode="numeric" value={age}
            onInput={(e) => setAge(+(e.target as HTMLInputElement).value)} />
        </div>

        <div class="field">
          <label>Sex</label>
          <div class="seg">
            <button class={sex === "male" ? "active" : ""} onClick={() => setSex("male")}>Male</button>
            <button class={sex === "female" ? "active" : ""} onClick={() => setSex("female")}>Female</button>
          </div>
        </div>

        <div class="field">
          <label>Height</label>
          <div style={{ display: "flex", gap: 10 }}>
            <input type="number" inputMode="numeric" value={ft}
              onInput={(e) => setFt(+(e.target as HTMLInputElement).value)} placeholder="ft" />
            <input type="number" inputMode="numeric" value={inch}
              onInput={(e) => setInch(+(e.target as HTMLInputElement).value)} placeholder="in" />
          </div>
        </div>

        <div class="field">
          <label>Current weight (lb)</label>
          <input type="number" inputMode="decimal" value={weightLb}
            onInput={(e) => setWeightLb(+(e.target as HTMLInputElement).value)} />
        </div>

        <div class="field" style={{ marginBottom: 0 }}>
          <label>Activity level</label>
          <select value={activity} onChange={(e) => setActivity((e.target as HTMLSelectElement).value as ActivityLevel)}>
            {ACTIVITY.map((a) => <option value={a.v} key={a.v}>{a.label}</option>)}
          </select>
        </div>
      </div>

      {preview && (
        <div class="card fade-in">
          <h2>Your starting targets</h2>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div class="big-number">{preview.kcal}<span class="unit"> kcal</span></div>
          </div>
          <div style={{ display: "flex", gap: 18, marginTop: 14 }}>
            <Macro label="Protein" v={preview.protein_g} color="var(--protein)" />
            <Macro label="Carbs" v={preview.carbs_g} color="var(--carbs)" />
            <Macro label="Fat" v={preview.fat_g} color="var(--fat)" />
          </div>
          <p class="muted" style={{ marginTop: 14 }}>{preview.rationale}</p>
        </div>
      )}

      <div class="err">{err}</div>
      <button class="btn" onClick={commit} disabled={busy || !preview}>
        {busy ? "Saving…" : "Start tracking"}
      </button>
    </div>
  );
}

function Macro({ label, v, color }: { label: string; v: number; color: string }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 650 }}>
        <span class="dot" style={{ background: color, width: 9, height: 9, borderRadius: "50%", display: "inline-block", marginRight: 6 }} />
        {v}<span class="unit">g</span>
      </div>
      <div class="lb" style={{ fontSize: 11, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    </div>
  );
}
